import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { theme } from "../../theme/colors";
import { castPollVote, removePollVote } from "../../utils/feedApi";

function getOptionVoteCount(option, verifiedCountsOnly) {
  if (verifiedCountsOnly) {
    return option.verified_vote_count ?? 0;
  }
  return option.vote_count ?? 0;
}

export default function PollVoteBlock({
  post,
  currentUserId,
  onAuthRequired,
  verifiedCountsOnly = false,
}) {
  const [options, setOptions] = useState(post.poll_options || []);
  const [viewerVotes, setViewerVotes] = useState(post.viewer_poll_votes || []);
  const [totalVotes, setTotalVotes] = useState(post.poll_total_votes || 0);
  const [verifiedTotalVotes, setVerifiedTotalVotes] = useState(post.verified_poll_total_votes || 0);
  const [pendingOptionId, setPendingOptionId] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setOptions(post.poll_options || []);
    setViewerVotes(post.viewer_poll_votes || []);
    setTotalVotes(post.poll_total_votes || 0);
    setVerifiedTotalVotes(post.verified_poll_total_votes || 0);
    setSelectedOptions([]);
    setPendingOptionId(null);
  }, [post.id, post.poll_options, post.viewer_poll_votes, post.poll_total_votes, post.verified_poll_total_votes]);

  const isMulti = !!post.allows_multiple_choices;
  const isAuthor = post.viewer_is_author ?? (post.user?.id === currentUserId || post.user_id === currentUserId);
  const hasVoted = viewerVotes.length > 0;
  const isGuest = !currentUserId;
  const showResults = hasVoted || isAuthor || isGuest;

  const displayTotalVotes = useMemo(() => {
    if (verifiedCountsOnly) {
      if (verifiedTotalVotes > 0) return verifiedTotalVotes;
      return options.reduce((sum, opt) => sum + getOptionVoteCount(opt, true), 0);
    }
    return totalVotes;
  }, [verifiedCountsOnly, verifiedTotalVotes, totalVotes, options]);

  const applyVoteResult = useCallback((data) => {
    setOptions(data.poll_options || []);
    setViewerVotes(data.viewer_poll_votes || []);
    setTotalVotes(data.poll_total_votes || 0);
    if (typeof data.verified_poll_total_votes === "number") {
      setVerifiedTotalVotes(data.verified_poll_total_votes);
    }
    setSelectedOptions([]);
  }, []);

  const canCastVote = useCallback(() => {
    if (!currentUserId) {
      onAuthRequired?.();
      return false;
    }
    return true;
  }, [currentUserId, onAuthRequired]);

  const handleSingleVote = useCallback(async (optionId) => {
    if (!canCastVote()) return;

    const alreadyVoted = viewerVotes.includes(optionId);

    setPendingOptionId(optionId);
    try {
      if (alreadyVoted) {
        const data = await removePollVote(post.id, optionId);
        applyVoteResult(data);
      } else {
        if (viewerVotes.length > 0) {
          await removePollVote(post.id, viewerVotes[0]);
        }
        const data = await castPollVote(post.id, optionId);
        applyVoteResult(data);
      }
    } catch (err) {
      console.error("Poll vote failed", err);
    } finally {
      setPendingOptionId(null);
    }
  }, [canCastVote, post.id, viewerVotes, applyVoteResult]);

  const handleUnvote = useCallback(async (optionId) => {
    if (!canCastVote()) return;
    setPendingOptionId(optionId);
    try {
      const data = await removePollVote(post.id, optionId);
      applyVoteResult(data);
    } catch (err) {
      console.error("Poll unvote failed", err);
    } finally {
      setPendingOptionId(null);
    }
  }, [canCastVote, post.id, applyVoteResult]);

  const handleMultiSubmit = useCallback(async () => {
    if (!canCastVote()) return;
    setSubmitting(true);
    try {
      let lastResult = null;
      for (const optId of selectedOptions) {
        lastResult = await castPollVote(post.id, optId);
      }
      if (lastResult) applyVoteResult(lastResult);
    } catch (err) {
      console.error("Poll vote failed", err);
    } finally {
      setSubmitting(false);
    }
  }, [canCastVote, post.id, selectedOptions, applyVoteResult]);

  const toggleSelection = (optionId) => {
    setSelectedOptions((prev) =>
      prev.includes(optionId) ? prev.filter((id) => id !== optionId) : [...prev, optionId]
    );
  };

  if (!options.length) return null;

  // ── Results view (after voting / author) ──────────────────────────────────

  if (showResults) {
    return (
      <Box sx={{ mt: 2, mb: 1 }}>
        {options.map((opt) => {
          const voteCount = getOptionVoteCount(opt, verifiedCountsOnly);
          const pct = displayTotalVotes > 0 ? Math.round((voteCount / displayTotalVotes) * 100) : 0;
          const isMyVote = viewerVotes.includes(opt.id);
          const busy = pendingOptionId === opt.id;

          const canInteract = !isAuthor && !busy && !pendingOptionId && !isGuest;

          return (
            <Box
              key={opt.id}
              onClick={() => {
                if (!canInteract) return;
                if (isMulti) {
                  if (isMyVote) handleUnvote(opt.id);
                  else handleSingleVote(opt.id);
                } else {
                  handleSingleVote(opt.id);
                }
              }}
              sx={{
                position: "relative",
                mb: 1,
                borderRadius: "10px",
                overflow: "hidden",
                border: isMyVote ? `2px solid ${theme.primary}` : "1px solid #e2e8f0",
                cursor: canInteract ? "pointer" : "default",
                bgcolor: "#fff",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  bottom: 0,
                  width: `${pct}%`,
                  bgcolor: isMyVote ? "rgba(4, 120, 87, 0.12)" : "rgba(15, 23, 42, 0.06)",
                  transition: "width 0.3s ease",
                }}
              />
              <Box sx={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "space-between", px: 1.5, py: 1.25 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                  {busy ? (
                    <CircularProgress size={18} sx={{ color: theme.primary }} />
                  ) : isMyVote ? (
                    isMulti ? <CheckBoxIcon sx={{ fontSize: 20, color: theme.primary }} /> : <RadioButtonCheckedIcon sx={{ fontSize: 20, color: theme.primary }} />
                  ) : (
                    isMulti ? <CheckBoxOutlineBlankIcon sx={{ fontSize: 20, color: "#94a3b8" }} /> : <RadioButtonUncheckedIcon sx={{ fontSize: 20, color: "#94a3b8" }} />
                  )}
                  <Typography sx={{ fontSize: 14, fontWeight: isMyVote ? 600 : 500, color: "#0f172a" }} noWrap>
                    {opt.text}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#64748b", ml: 1, flexShrink: 0 }}>
                  {voteCount} ({pct}%)
                </Typography>
              </Box>
            </Box>
          );
        })}
        <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.5 }}>
          {displayTotalVotes} {displayTotalVotes === 1 ? "vote" : "votes"}
          {verifiedCountsOnly ? " from verified users" : ""}
        </Typography>
      </Box>
    );
  }

  // ── Voting view (before vote) ───────────────────────────────────────────

  if (isMulti) {
    return (
      <Box sx={{ mt: 2, mb: 1 }}>
        {options.map((opt) => {
          const selected = selectedOptions.includes(opt.id);
          return (
            <Box
              key={opt.id}
              onClick={() => toggleSelection(opt.id)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 1,
                p: 1.25,
                borderRadius: "10px",
                border: selected ? `2px solid ${theme.primary}` : "1px solid #e2e8f0",
                cursor: "pointer",
                bgcolor: selected ? "rgba(4, 120, 87, 0.04)" : "#fff",
              }}
            >
              {selected ? (
                <CheckBoxIcon sx={{ fontSize: 20, color: theme.primary }} />
              ) : (
                <CheckBoxOutlineBlankIcon sx={{ fontSize: 20, color: "#94a3b8" }} />
              )}
              <Typography sx={{ fontSize: 14, fontWeight: selected ? 600 : 500, color: "#0f172a" }}>
                {opt.text}
              </Typography>
            </Box>
          );
        })}
        <Button
          variant="contained"
          disabled={selectedOptions.length === 0 || submitting}
          onClick={handleMultiSubmit}
          sx={{
            mt: 0.5,
            background: theme.gradient,
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "10px",
            boxShadow: "none",
            "&:hover": { background: theme.gradient, opacity: 0.92 },
          }}
        >
          {submitting ? <CircularProgress size={20} color="inherit" /> : "Submit vote"}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2, mb: 1 }}>
      {options.map((opt) => {
        const busy = pendingOptionId === opt.id;
        return (
          <Box
            key={opt.id}
            onClick={() => !busy && handleSingleVote(opt.id)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1,
              p: 1.25,
              borderRadius: "10px",
              border: "1px solid #e2e8f0",
              cursor: busy ? "default" : "pointer",
              bgcolor: "#fff",
              "&:hover": busy ? {} : { borderColor: theme.primary, bgcolor: "rgba(4, 120, 87, 0.04)" },
            }}
          >
            {busy ? (
              <CircularProgress size={18} sx={{ color: theme.primary }} />
            ) : (
              <RadioButtonUncheckedIcon sx={{ fontSize: 20, color: "#94a3b8" }} />
            )}
            <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#0f172a" }}>
              {opt.text}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}
